// native
import { catchError, concatMap, delay, map, retryWhen, tap, timeout } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';

//addon
import { TranslateService } from '@ngx-translate/core';

// services
import { AuthService } from './auth.service';
import { ErrorService } from './error.service';

const REQUEST_WAITING_TIME = 10 * 1000;
const MAX_REQUEST_RETRIES = 3;
const REQUEST_RETRY_DELAY = 2 * 1000;
const REQUEST_TIMEOUT_ERROR = 'requestTimeoutError';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private translateService: TranslateService,
    private errorService: ErrorService
  ) { }

  public get(url: string, skipRetries = false): Observable<object | Array<object> | string> {
    if (skipRetries) {
      return this.httpClient.get(url, { responseType: 'json' }).pipe(
        catchError(err => this.handleError(err))
      );
    }
    
    return this.httpClient.get(url, { responseType: 'json' }).pipe(
      timeout(REQUEST_WAITING_TIME),
      retryWhen(errors =>
        errors.pipe(
          concatMap((error, count) => {
            if (count >= MAX_REQUEST_RETRIES) {
              return throwError(REQUEST_TIMEOUT_ERROR);
            }
            return of(error).pipe(delay(REQUEST_RETRY_DELAY));
          })
        )
      ),
      catchError(err => this.handleError(err))
    );
  }

  public getBlob(url: string): Observable<Blob | string> {
    return this.httpClient.get(url, { responseType: 'blob' }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  public downloadFile(url: string, headers: { [header: string]: string; } = null): Observable<void> {
    return this.httpClient.get(url, { responseType: 'blob', observe: 'response', headers: headers }).pipe(
      map(response => {
        return {
          blob: response.body,
          filename: response.headers.get('content-disposition')?.split('filename="')[1]?.split('"')[0] || 'report'
        };
      }),
      tap(file => {
        let a = document.createElement('a');
        a.href = window.URL.createObjectURL(file.blob);
        a.download = file.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(a.href);
      }),
      map(file => undefined),
      catchError(err => this.handleError(err))
    );
  }

  public delete(url: string): Observable<object | Array<object> | string> {
    return this.httpClient.delete(url, { responseType: 'json' }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  public put(url: string, body: object = null): Observable<object | Array<object> | string> {
    return this.httpClient.put(url, body, { responseType: 'json' }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  public post(url: string, body: object = null, isTextResponse = false): Observable<object | Array<object> | string> {
    if (isTextResponse) {
      return this.httpClient.post(url, body, { responseType: 'text' }).pipe(
        catchError(err => this.handleError(err))
      );
    }
    return this.httpClient.post(url, body, { responseType: 'json' }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  private handleError(errorResponse: Response | any): Observable<any> {
    // error can get from server in different forms
    const error: any = errorResponse?.error?.message
      || errorResponse?.error?.detail
      || errorResponse?.detail
      || errorResponse?.error;

    if (errorResponse === REQUEST_TIMEOUT_ERROR)
      return throwError(this.translateService.instant('requestTimeoutError'));

    if (errorResponse?.status === 0)
      return throwError(this.translateService.instant('networkConnectionUnstable'));

    if ([500, 502, 504].includes(errorResponse?.status)) {
      this.errorService.handleServerErrors(error);
      return;
    }

    if ([401, 403].includes(errorResponse?.status)) {
      this.errorService.handleAuthErrors(error);
      this.authService.logout();
      return;
    }

    return throwError(error);
  }
}
